import axios from 'axios';

import { API_URL } from './config';

let conected = window.navigator.onLine;

window.addEventListener('online', function () {
  conected = true;
});

window.addEventListener('offline', function () {
  conected = false;
});

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Rotas que não recebem Authorization nem disparam o fluxo de refresh
// (login, refresh e endpoints públicos).
const isAuthFreeUrl = url =>
  !!url && (url.includes('api-token') || url.includes('/public/'));

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('@app:user');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const redirectToLogin = () => {
  localStorage.removeItem('@app:user');
  // Navegação dura: zera o estado do Redux e evita loop com o useEffect do
  // login. Idempotente: só redireciona se estiver dentro da área logada.
  if (window.location.pathname.startsWith('/app')) {
    window.location.assign('/');
  }
};

const isSessionInvalid = error => {
  if (error === 'SESSION_EXPIRED') return true;
  const status = error?.response?.status;
  return status === 400 || status === 401 || status === 403;
};

async function refreshSession() {
  const user = getStoredUser();
  const refreshToken = user?.uid?.refresh_token;
  if (!refreshToken) throw 'SESSION_EXPIRED';

  const { data } = await api.post(api.authRefreshURL, { refresh: refreshToken });

  const updated = {
    ...user,
    uid: {
      ...user.uid,
      token: data.access,
      // A API rotaciona o refresh token — persistir o novo é obrigatório
      ...(data.refresh ? { refresh_token: data.refresh } : {}),
    },
  };
  localStorage.setItem('@app:user', JSON.stringify(updated));
}

// Anexa o Bearer lido do storage a cada request (nunca congelado em módulo)
api.interceptors.request.use(config => {
  if (isAuthFreeUrl(config.url)) {
    delete config.headers.Authorization;
    return config;
  }

  const token = getStoredUser()?.uid?.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let isRefreshing = false;
let failedRequestsQueue = [];

const failQueue = error => {
  const queue = failedRequestsQueue;
  failedRequestsQueue = [];
  queue.forEach(request => request.onFailure(error));
};

const flushQueue = () => {
  const queue = failedRequestsQueue;
  failedRequestsQueue = [];
  queue.forEach(request => request.onSuccess());
};

api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (!conected) return Promise.reject('NO_INTERNET');

    const originalConfig = error?.config;
    const status = error?.response?.status;

    if (status !== 401 || !originalConfig || isAuthFreeUrl(originalConfig.url)) {
      return Promise.reject(error);
    }

    if (originalConfig._retry) {
      // 401 mesmo depois de refresh bem-sucedido: sessão irrecuperável
      failQueue(error);
      redirectToLogin();
      return Promise.reject(error);
    }
    originalConfig._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;

      refreshSession()
        .then(() => {
          flushQueue();
        })
        .catch(refreshError => {
          failQueue(refreshError);
          if (isSessionInvalid(refreshError)) {
            redirectToLogin();
          }
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    return new Promise((resolve, reject) => {
      failedRequestsQueue.push({
        // O request interceptor reaplica o Authorization com o token novo
        onSuccess: () => resolve(api(originalConfig)),
        onFailure: failureError => reject(failureError),
      });
    });
  }
);

api.authURL = `${API_URL}/api-token-auth/web-admin/`;
api.authRefreshURL = `${API_URL}/api-token-refresh/`;
api.url = API_URL;
